import { type ReactNode } from 'react';
import {
  Palette,
  Type,
  Ruler,
  Box,
  LayoutGrid,
  BarChart3,
  Navigation,
  FileInput,
  BookOpen,
  Accessibility,
  LayoutTemplate,
  MessageSquareText,
  type LucideIcon,
} from 'lucide-react';

import { VisaoGeralSection } from './sections/VisaoGeralSection';
import { PadroesAdminSection } from './sections/PadroesAdminSection';
import { AcessibilidadeSection } from './sections/AcessibilidadeSection';
import { ConteudoSection } from './sections/ConteudoSection';

import { CoresTab } from './CoresTab';
import { TipografiaTab } from './TipografiaTab';
import { EspacamentoTab } from './EspacamentoTab';
import { ComponentesBaseTab } from './ComponentesBaseTab';
import { NavegacaoTab } from './NavegacaoTab';
import { FormulariosTab } from './FormulariosTab';
import { ComponentesSistemaTab } from './ComponentesSistemaTab';
import { GraficosTab } from './GraficosTab';

import { CATALOGO, type IdDaSecao, type ItemDoCatalogo } from './catalogo';

export interface DesignSystemSection {
  id: IdDaSecao;
  label: string;
  icon: LucideIcon;
  /** Frase de abertura — usada na aba, no sumário e no cabeçalho da folha A4. */
  intro: string;
  render: () => ReactNode;
  /**
   * O que a seção mostra, item por item (`catalogo.ts`): a busca procura aqui,
   * e cada item tem a sua âncora (`data-search-id`) na seção renderizada.
   */
  itens: readonly ItemDoCatalogo[];
  /**
   * Unidades atômicas da seção para o documento impresso, na ordem. Ausente,
   * a seção inteira é um bloco só (`print/secoesDoDocumento.tsx`).
   */
  blocos?: () => ReactNode[];
}

type SemItens = Omit<DesignSystemSection, 'itens'>;

/**
 * Fonte única das seções do Design System.
 * A tela (/admin/design-system), a busca e o PDF consomem exatamente este
 * registro — não existe conteúdo que apareça em apenas um deles.
 */
const SECOES: readonly SemItens[] = [
  {
    id: 'visao-geral',
    label: 'Visão geral',
    icon: BookOpen,
    intro: 'Para que serve o Design System, princípios e como uma tela é composta.',
    render: () => <VisaoGeralSection />,
  },
  {
    id: 'cores',
    label: 'Cores',
    icon: Palette,
    intro: 'Paleta institucional, cores semânticas, neutras e verificação de contraste.',
    render: () => <CoresTab />,
  },
  {
    id: 'tipografia',
    label: 'Tipografia',
    icon: Type,
    intro: 'Escalas de texto, pesos e uso recomendado das famílias tipográficas.',
    render: () => <TipografiaTab />,
  },
  {
    id: 'espacamento',
    label: 'Espaçamento e layout',
    icon: Ruler,
    intro: 'Escala de espaçamento, arredondamentos e pontos de quebra responsivos.',
    render: () => <EspacamentoTab />,
  },
  {
    id: 'componentes-base',
    label: 'Componentes base',
    icon: Box,
    intro:
      'Botões, selos, cartões e demais peças elementares da interface, com a grade de primitivos.',
    render: () => <ComponentesBaseTab />,
  },
  {
    id: 'navegacao',
    label: 'Navegação',
    icon: Navigation,
    intro: 'A casca das áreas internas, o portal público e a entrada pelo gov.br.',
    render: () => <NavegacaoTab />,
  },
  {
    id: 'formularios',
    label: 'Formulários',
    icon: FileInput,
    intro: 'Campos, agrupamentos, validação e formatos de entrada.',
    render: () => <FormulariosTab />,
  },
  {
    id: 'componentes-sistema',
    label: 'Componentes do sistema',
    icon: LayoutGrid,
    intro: 'Peças compostas específicas do TEG: listagens, valores, detalhes e indicadores.',
    render: () => <ComponentesSistemaTab />,
  },
  {
    id: 'graficos',
    label: 'Gráficos e retorno ao usuário',
    icon: BarChart3,
    intro: 'Visualizações de dados e o retorno ao usuário: avisos, falhas e os estados da tela.',
    render: () => <GraficosTab />,
  },
  {
    id: 'padroes',
    label: 'Padrões de tela',
    icon: LayoutTemplate,
    intro: 'A anatomia obrigatória das telas administrativas e suas regras transversais.',
    render: () => <PadroesAdminSection />,
  },
  {
    id: 'acessibilidade',
    label: 'Acessibilidade',
    icon: Accessibility,
    intro: 'Critérios WCAG 2.1 AA adotados e como são verificados automaticamente.',
    render: () => <AcessibilidadeSection />,
  },
  {
    id: 'conteudo',
    label: 'Conteúdo e linguagem',
    icon: MessageSquareText,
    intro: 'Vocabulário oficial, tom por persona e regras de escrita da interface.',
    render: () => <ConteudoSection />,
  },
];

export const designSystemSections: DesignSystemSection[] = SECOES.map((secao) => ({
  ...secao,
  itens: CATALOGO[secao.id],
}));
